/**
 * ArgoCD Management Tab
 * ArgoCD 管理 - 连通性、Token、版本、应用概览
 */

import { useEffect, useState, useCallback } from 'react';
import {
  InfoCard,
  StructuredMetadataTable,
} from '@backstage/core-components';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import RefreshIcon from '@material-ui/icons/Refresh';
import CloudIcon from '@material-ui/icons/Cloud';
import AppsIcon from '@material-ui/icons/Apps';
import PersonIcon from '@material-ui/icons/Person';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { useApi, discoveryApiRef, identityApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  statCard: {
    padding: theme.spacing(2.5),
    textAlign: 'center',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1,
  },
  statusBanner: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  success: {
    background: 'rgba(76, 175, 80, 0.08)',
    border: '1px solid rgba(76, 175, 80, 0.3)',
  },
  error: {
    background: 'rgba(244, 67, 54, 0.08)',
    border: '1px solid rgba(244, 67, 54, 0.3)',
  },
}));

interface ArgoCDCheckResult {
  configured: boolean;
  instances: Array<{ name: string; url: string; tokenMasked: string }>;
  connectionStatus?: 'connected' | 'error';
  connectionError?: string;
  version?: { version: string; buildDate?: string; goVersion?: string; compiler?: string };
  userInfo?: { loggedIn: boolean; username?: string; iss?: string };
  appCount?: number;
  checkedAt: string;
}

export const ArgoCDManagementTab: React.FC = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const [result, setResult] = useState<ArgoCDCheckResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();
      const resp = await fetch(
        `${baseUrl}/external-services/argocd/check${force ? '?refresh=true' : ''}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setResult(await resp.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [discoveryApi, identityApi]);

  useEffect(() => { check(); }, [check]);

  if (loading && !result) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (result && !result.configured) {
    return (
      <Box p={3}>
        <Alert severity="info">未在配置文件中找到 ArgoCD 配置 (argocd.appLocatorMethods)</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          size="small"
          variant="outlined"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={() => check(true)}
          disabled={loading}
          style={{ textTransform: 'none' }}
        >
          {loading ? '检测中...' : '重新检测'}
        </Button>
      </Box>

      {result && (
        <>
          {/* 统计卡片 */}
          <Grid container spacing={2} style={{ marginBottom: 24 }}>
            <Grid item xs={12} sm={3}>
              <Paper className={classes.statCard} variant="outlined">
                <CloudIcon style={{ fontSize: 28, color: result.connectionStatus === 'connected' ? '#4caf50' : '#f44336' }} />
                <Typography variant="caption" color="textSecondary">连通性</Typography>
                <Typography style={{ fontWeight: 600, color: result.connectionStatus === 'connected' ? '#4caf50' : '#f44336' }}>
                  {result.connectionStatus === 'connected' ? '已连接' : '异常'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper className={classes.statCard} variant="outlined">
                <InfoOutlinedIcon style={{ fontSize: 28, color: '#1976d2' }} />
                <Typography variant="caption" color="textSecondary">版本</Typography>
                <Typography style={{ fontWeight: 600, color: '#1976d2', fontSize: '0.95rem' }}>
                  {result.version?.version || '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper className={classes.statCard} variant="outlined">
                <PersonIcon style={{ fontSize: 28, color: '#ff9800' }} />
                <Typography variant="caption" color="textSecondary">认证用户</Typography>
                <Typography style={{ fontWeight: 600, color: '#ff9800', fontSize: '0.95rem' }}>
                  {result.userInfo?.username || '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper className={classes.statCard} variant="outlined">
                <AppsIcon style={{ fontSize: 28, color: '#7b1fa2' }} />
                <Typography variant="caption" color="textSecondary">应用数量</Typography>
                <Typography className={classes.statNumber} style={{ color: '#7b1fa2' }}>
                  {result.appCount ?? '-'}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* 连接状态 */}
          <InfoCard title="连接状态">
            <Box className={`${classes.statusBanner} ${result.connectionStatus === 'connected' ? classes.success : classes.error}`}>
              {result.connectionStatus === 'connected' ? (
                <CheckCircleIcon style={{ color: '#4caf50' }} />
              ) : (
                <ErrorIcon style={{ color: '#f44336' }} />
              )}
              <Box>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  {result.connectionStatus === 'connected' ? 'ArgoCD API 连通' : result.connectionError || '连接失败'}
                </Typography>
              </Box>
            </Box>

            {result.version && (
              <StructuredMetadataTable metadata={{
                '版本': result.version.version,
                '构建时间': result.version.buildDate || '-',
                'Go 版本': result.version.goVersion || '-',
                '编译器': result.version.compiler || '-',
              }} />
            )}

            {result.userInfo && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                  Token 信息
                </Typography>
                <StructuredMetadataTable metadata={{
                  '已登录': result.userInfo.loggedIn ? '是' : '否',
                  '用户名': result.userInfo.username || '-',
                  'Issuer': result.userInfo.iss || '-',
                }} />
              </Box>
            )}
          </InfoCard>

          {/* 实例列表 */}
          {result.instances.length > 0 && (
            <Box mt={2}>
              <InfoCard title={`实例配置 (${result.instances.length})`}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>实例名</TableCell>
                        <TableCell>URL</TableCell>
                        <TableCell>Token</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.instances.map(inst => (
                        <TableRow key={inst.name}>
                          <TableCell>
                            <Typography variant="body2" style={{ fontWeight: 500 }}>{inst.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {inst.url}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={inst.tokenMasked} size="small" variant="outlined"
                              style={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </InfoCard>
            </Box>
          )}

          <Box mt={2} display="flex" justifyContent="flex-end">
            <Typography variant="caption" color="textSecondary">
              检测时间: {new Date(result.checkedAt).toLocaleString('zh-CN')}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};
